import { useState } from 'react';
import { Field } from './Field.js';
import { DisplayNameField, checkDisplayNameError } from './DisplayNameField.js';
import { EmailField } from './EmailField.js';
import { checkEmail } from './emailValidation.js';
import { PasswordField } from '../PasswordField.js';
import { RadioGroup, CheckboxGroup, StateSelect } from './inputs.js';
import { LegalLinks } from './LegalLinks.js';
import { InfoModal } from './InfoModal.js';
import { SmsConsent } from './SmsConsent.js';
import type { WizardStep } from './RegistrationWizard.js';
import { GUIDE_AGE_OPTIONS, ASL_YEARS_OPTIONS, INTERESTED_CDI_OPTIONS, AVAILABILITY_SLOTS, EXPERTISE_AREAS } from './types.js';

const YES_NO = [{ id: 'yes', label: 'Yes' }, { id: 'no', label: 'No' }] as const;

const personal: WizardStep = {
  key: 'personal', title: 'Personal information',
  validate: (d) => {
    const e: Record<string, string> = {};
    if (!d.fullName.trim()) e.fullName = 'Required';
    if (d.password.length < 6) e.password = 'At least 6 characters';
    if (!d.displayName.trim()) e.displayName = 'Required';
    if (!d.ageRange) e.ageRange = 'Select an age range';
    if (!d.email.trim()) e.email = 'Required';
    else {
      const msg = checkEmail(d.email).error;
      if (msg) e.email = msg;
    }
    if (!d.phone?.trim()) e.phone = 'Required';
    else if (!d.smsConsent) e.smsConsent = 'Please choose one of the SMS options.';
    if (!d.city?.trim()) e.city = 'Required';
    if (!d.state) e.state = 'Required';
    return e;
  },
  validateAsync: async (d): Promise<Record<string, string>> => {
    const msg = await checkDisplayNameError(d.displayName);
    return msg ? { displayName: msg } : {};
  },
  Component: ({ data, update, errors }) => (
    <>
      <Field label="Full name" error={errors.fullName}><input className="input" value={data.fullName} onChange={(e) => update({ fullName: e.target.value })} /></Field>
      <PasswordField label="Password" value={data.password} error={errors.password}
        onChange={(v) => update({ password: v })} autoComplete="new-password" minLength={6} />
      <DisplayNameField value={data.displayName} update={update} error={errors.displayName} />
      <RadioGroup legend="Age range (must be 18 or older)" options={GUIDE_AGE_OPTIONS} value={data.ageRange} onChange={(id) => update({ ageRange: id })} error={errors.ageRange} />
      <EmailField label="Email" value={data.email} error={errors.email}
        onChange={(v) => update({ email: v })} autoComplete="email" />
      <Field label="Mobile phone number" error={errors.phone}><input className="input" type="tel" value={data.phone ?? ''} onChange={(e) => update({ phone: e.target.value })} /></Field>
      {data.phone?.trim() ? (
        <SmsConsent value={data.smsConsent} onChange={(smsConsent) => update({ smsConsent })} error={errors.smsConsent} />
      ) : null}
      <Field label="City of current residence" error={errors.city}><input className="input" value={data.city ?? ''} onChange={(e) => update({ city: e.target.value })} /></Field>
      <Field label="State of current residence" error={errors.state}><StateSelect value={data.state ?? ''} onChange={(id) => update({ state: id })} /></Field>
      <Field label="Ethnicity (optional)" hint="Used only for aggregate community data; not shared publicly."><input className="input" value={data.ethnicity ?? ''} onChange={(e) => update({ ethnicity: e.target.value })} /></Field>
    </>
  ),
};

const background: WizardStep = {
  key: 'background', title: 'ASL & professional background',
  validate: (d) => {
    const e: Record<string, string> = {};
    if (!d.aslYears) e.aslYears = 'Select one';
    if (d.isCdi === undefined) e.isCdi = 'Please choose';
    if (d.isCdi === false && !d.interestedInCdi) e.interestedInCdi = 'Please choose';
    if (!(d.expertiseAreas ?? []).length) e.expertiseAreas = 'Select at least one area';
    if ((d.expertiseAreas ?? []).includes('other') && !d.expertiseAreaOther?.trim()) e.expertiseAreaOther = 'Please specify';
    if (!(d.availabilitySlots ?? []).length) e.availabilitySlots = 'Pick at least one';
    return e;
  },
  Component: ({ data, update, errors }) => {
    const slots = data.availabilitySlots ?? [];
    const toggle = (id: string) => update({ availabilitySlots: slots.includes(id) ? slots.filter((x) => x !== id) : [...slots, id] });
    const areas = data.expertiseAreas ?? [];
    const toggleArea = (id: string) => update({ expertiseAreas: areas.includes(id) ? areas.filter((x) => x !== id) : [...areas, id] });
    return (
      <>
        <RadioGroup legend="Years of ASL experience" options={ASL_YEARS_OPTIONS} value={data.aslYears ?? ''} onChange={(id) => update({ aslYears: id })} error={errors.aslYears} />
        <RadioGroup legend="Are you a Certified Deaf Interpreter (CDI) / Deaf Interpreter (DI)?" options={YES_NO}
          value={data.isCdi === undefined ? '' : data.isCdi ? 'yes' : 'no'} onChange={(id) => update({ isCdi: id === 'yes' })} error={errors.isCdi} />
        {data.isCdi === false ? (
          <RadioGroup legend="If no, are you interested in becoming one?" options={INTERESTED_CDI_OPTIONS} value={data.interestedInCdi ?? ''} onChange={(id) => update({ interestedInCdi: id })} error={errors.interestedInCdi} />
        ) : null}
        <CheckboxGroup legend="Area of expertise" options={EXPERTISE_AREAS} values={areas} onToggle={toggleArea} error={errors.expertiseAreas} />
        {areas.includes('other') ? (
          <Field label="Please specify" error={errors.expertiseAreaOther}>
            <input className="input" value={data.expertiseAreaOther ?? ''} onChange={(e) => update({ expertiseAreaOther: e.target.value })} />
          </Field>
        ) : null}
        <CheckboxGroup legend="Preferred availability" options={AVAILABILITY_SLOTS} values={slots} onToggle={toggle} error={errors.availabilitySlots} />
      </>
    );
  },
};

const verification: WizardStep = {
  key: 'verification', title: 'Verification & compliance',
  validate: (d): Record<string, string> => (d.bgCheckConsent === true ? {} : { bgCheckConsent: 'A background check is required to proceed.' }),
  Component: ({ data, update, errors }) => {
    const [showReject, setShowReject] = useState(false);
    return (
      <>
        <RadioGroup legend="Do you agree to a background check?" options={YES_NO}
          value={data.bgCheckConsent === undefined ? '' : data.bgCheckConsent ? 'yes' : 'no'}
          onChange={(id) => { const yes = id === 'yes'; update({ bgCheckConsent: yes }); if (!yes) setShowReject(true); }}
          error={errors.bgCheckConsent} />
        <p className="hint">We may use a third-party service (e.g., Checkr or Sterling) to verify identity and protect both Deaf Guides and Learners. You'll complete the background check itself in a later step, after your account is confirmed.</p>
        {showReject ? (
          <InfoModal title="Background check required" onClose={() => setShowReject(false)}>
            <p>Thank you for your interest in becoming a Deaf Guide. Because a background check is required for safety and compliance, we cannot proceed with your application at this time.</p>
          </InfoModal>
        ) : null}
      </>
    );
  },
};

const agreements: WizardStep = {
  key: 'agreements', title: 'Agreements',
  validate: (d) => {
    const e: Record<string, string> = {};
    if (!d.agreementAccepted) e.agreementAccepted = 'You must agree to the Privacy Policy & Terms of Use.';
    if (!d.codeOfConductConsent) e.codeOfConductConsent = 'You must agree to the Code of Conduct.';
    return e;
  },
  Component: ({ data, update, errors }) => {
    return (
      <>
        <label className="inline-flex items-start gap-2">
          <input type="checkbox" aria-label="Agree to Privacy and Terms" checked={data.agreementAccepted ?? false} onChange={(e) => update({ agreementAccepted: e.target.checked })} />
          <span className="hint">I have read and agree to the SignWow Privacy Policy and Terms of Use.</span>
        </label>
        <div className="flex flex-col gap-1">
          <LegalLinks />
        </div>
        {errors.agreementAccepted ? <span className="text-[0.8rem] text-status-error dark:text-red-400">{errors.agreementAccepted}</span> : null}
        <label className="inline-flex items-start gap-2 mt-2">
          <input type="checkbox" aria-label="Agree to Code of Conduct" checked={data.codeOfConductConsent ?? false} onChange={(e) => update({ codeOfConductConsent: e.target.checked })} />
          <span className="hint">I agree to show professionalism, patience, and cultural respect during all ASL interactions.</span>
        </label>
        {errors.codeOfConductConsent ? <span className="text-[0.8rem] text-status-error dark:text-red-400">{errors.codeOfConductConsent}</span> : null}
      </>
    );
  },
};

const signature: WizardStep = {
  key: 'signature', title: 'Digital signature',
  validate: (d): Record<string, string> => (d.signatureAgree ? {} : { signatureAgree: 'You must agree to submit your application.' }),
  Component: ({ data, update, errors }) => (
    <>
      <p className="hint">By clicking "Submit Application," you confirm:</p>
      <ul className="list-disc pl-5 text-[0.85rem] text-ink-600 dark:text-ink-300 space-y-1">
        <li>You are 18 or older.</li>
        <li>All information provided is true and accurate.</li>
        <li>You agree to comply with SignWow's Terms, Privacy Policy, and Code of Conduct.</li>
      </ul>
      <label className="inline-flex items-start gap-2 mt-2">
        <input type="checkbox" aria-label="I Agree, digital signature" checked={data.signatureAgree ?? false} onChange={(e) => update({ signatureAgree: e.target.checked })} />
        <span className="hint">I Agree (this serves as your digital signature).</span>
      </label>
      {errors.signatureAgree ? <span className="text-[0.8rem] text-status-error dark:text-red-400">{errors.signatureAgree}</span> : null}
    </>
  ),
};

export const guideSteps: WizardStep[] = [personal, background, verification, agreements, signature];
